import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Lock, Tag, Banknote, CreditCard } from "lucide-react";

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

export default function Checkout() {
    const { items, preview, clear } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [coupon, setCoupon] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [discount, setDiscount] = useState(0);
    // "razorpay" (UPI/Card/Netbanking) or "cod" (cash on delivery)
    const [paymentMethod, setPaymentMethod] = useState("razorpay");
    // Small handling fee added to COD orders. Backend still owns the source of truth.
    const COD_FEE = 40; // ₹40, in rupees for UI-only preview

    const [address, setAddress] = useState({
        full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India", label: "Home",
    });
    const [email, setEmail] = useState("");

    useEffect(() => {
        if (items.length === 0) navigate("/cart");
    }, [items, navigate]);

    useEffect(() => {
        if (user) {
            setEmail(user.email);
            setAddress(prev => ({ ...prev, full_name: user.name || "", phone: user.phone || "" }));
            // Try to load addresses
            api.get("/auth/addresses").then(res => {
                const def = res.data.find(a => a.is_default) || res.data[0];
                if (def) setAddress({ ...def });
            }).catch(() => {});
        }
    }, [user]);

    const handleApplyCoupon = async () => {
        if (!coupon) return;
        try {
            const res = await api.post("/cart/apply-coupon", { code: coupon.toUpperCase(), subtotal: preview.subtotal });
            setAppliedCoupon(res.data);
            setDiscount(res.data.discount);
            toast.success(`Coupon ${res.data.code} applied`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invalid coupon");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // validate
        for (const k of ["full_name", "phone", "line1", "city", "state", "pincode"]) {
            if (!address[k]) { toast.error("Please fill all required fields"); return; }
        }
        if (!user && !email) { toast.error("Email is required"); return; }
        
        setSubmitting(true);
        try {
            const orderPayload = {
                items: items.map(i => ({ product_id: i.product_id, variant_sku: i.variant_sku, qty: i.qty })),
                address,
                coupon_code: appliedCoupon?.code || null,
                customer_email: email || user?.email,
                customer_name: address.full_name,
                customer_phone: address.phone,
                payment_method: paymentMethod,
            };

            // Cash on Delivery path — no Razorpay round-trip needed
            if (paymentMethod === "cod") {
                const res = await api.post("/orders/create", orderPayload);
                const { order } = res.data;
                clear();
                toast.success("Order placed! Pay on delivery.");
                navigate(`/order/confirmed/${order.id}`);
                return;
            }

            const ok = await loadRazorpayScript();
            if (!ok) { toast.error("Failed to load Razorpay"); setSubmitting(false); return; }

            const res = await api.post("/orders/create", orderPayload);
            const { order, razorpay } = res.data;

            const options = {
                key: razorpay.key_id,
                amount: razorpay.amount,
                currency: razorpay.currency,
                name: "X-Plosiv",
                description: `Order ${order.order_number}`,
                order_id: razorpay.order_id,
                prefill: {
                    name: address.full_name,
                    email: email || user?.email || "",
                    contact: address.phone,
                },
                theme: { color: "#121214" },
                handler: async (resp) => {
                    try {
                        await api.post("/payments/verify", {
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                            order_id: order.id,
                        });
                        clear();
                        toast.success("Payment successful!");
                        navigate(`/order/confirmed/${order.id}`);
                    } catch (err) {
                        toast.error("Payment verification failed");
                    }
                },
                modal: {
                    ondismiss: () => {
                        toast.info("Payment cancelled. Your bag is saved.");
                        setSubmitting(false);
                    }
                }
            };
            const rp = new window.Razorpay(options);
            rp.on("payment.failed", (resp) => {
                toast.error(`Payment failed: ${resp.error.description}`);
                setSubmitting(false);
            });
            rp.open();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start checkout");
            setSubmitting(false);
        }
    };

    const finalTotal = (preview.total - discount) + (paymentMethod === "cod" ? COD_FEE * 100 : 0);

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-testid="checkout-page">
            <h1 className="font-display text-5xl md:text-6xl">Checkout</h1>
            <p className="text-muted-foreground mt-2">Almost there — secure payment via Razorpay.</p>

            <form onSubmit={handleSubmit} className="mt-10 grid lg:grid-cols-[1fr_400px] gap-10">
                <div className="space-y-8">
                    <section className="dw-section p-6 sm:p-8">
                        <h2 className="font-display text-2xl mb-5">Contact</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Email" type="email" required value={email} onChange={setEmail} testid="checkout-email-input" />
                            <Field label="Phone" required value={address.phone} onChange={(v) => setAddress(a => ({ ...a, phone: v }))} testid="checkout-phone-input" />
                        </div>
                    </section>

                    <section className="dw-section p-6 sm:p-8">
                        <h2 className="font-display text-2xl mb-5">Shipping address</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Full name" required value={address.full_name} onChange={(v) => setAddress(a => ({ ...a, full_name: v }))} testid="checkout-fullname-input" />
                            <Field label="Address line 1" required value={address.line1} onChange={(v) => setAddress(a => ({ ...a, line1: v }))} testid="checkout-line1-input" />
                            <Field label="Address line 2" value={address.line2} onChange={(v) => setAddress(a => ({ ...a, line2: v }))} testid="checkout-line2-input" />
                            <Field label="City" required value={address.city} onChange={(v) => setAddress(a => ({ ...a, city: v }))} testid="checkout-city-input" />
                            <Field label="State" required value={address.state} onChange={(v) => setAddress(a => ({ ...a, state: v }))} testid="checkout-state-input" />
                            <Field label="PIN code" required value={address.pincode} onChange={(v) => setAddress(a => ({ ...a, pincode: v }))} testid="checkout-pincode-input" />
                        </div>
                    </section>

                    <section className="dw-section p-6 sm:p-8">
                        <h2 className="font-display text-2xl mb-5">Payment</h2>
                        <div className="space-y-3">
                            {/* Razorpay */}
                            <label
                                className={`flex items-center gap-4 rounded-2xl border p-4 cursor-pointer transition-all ${
                                    paymentMethod === "razorpay"
                                        ? "border-foreground bg-white/90 shadow-[0_6px_20px_rgba(0,0,0,0.08)]"
                                        : "border-[#E7DED2] bg-white/70 hover:border-foreground/40"
                                }`}
                                data-testid="checkout-pay-option-razorpay"
                            >
                                <input
                                    type="radio"
                                    name="payment"
                                    value="razorpay"
                                    checked={paymentMethod === "razorpay"}
                                    onChange={() => setPaymentMethod("razorpay")}
                                    className="sr-only"
                                />
                                <div className={`h-11 w-11 rounded-full flex items-center justify-center ${paymentMethod === "razorpay" ? "bg-[#121214] text-white" : "bg-secondary text-foreground"}`}>
                                    <CreditCard className="h-5 w-5" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium">Pay online</p>
                                        <span className="text-[10px] font-mono-ui tracking-[0.14em] uppercase text-[#2F7D5A] bg-[#2F7D5A]/10 px-2 py-0.5 rounded-full">Recommended</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">UPI · Cards · Net banking · Wallets — powered by Razorpay</p>
                                </div>
                                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === "razorpay" ? "border-foreground" : "border-muted-foreground/40"}`}>
                                    {paymentMethod === "razorpay" && <span className="h-2.5 w-2.5 rounded-full bg-foreground" />}
                                </div>
                            </label>

                            {/* Cash on Delivery */}
                            <label
                                className={`flex items-center gap-4 rounded-2xl border p-4 cursor-pointer transition-all ${
                                    paymentMethod === "cod"
                                        ? "border-foreground bg-white/90 shadow-[0_6px_20px_rgba(0,0,0,0.08)]"
                                        : "border-[#E7DED2] bg-white/70 hover:border-foreground/40"
                                }`}
                                data-testid="checkout-pay-option-cod"
                            >
                                <input
                                    type="radio"
                                    name="payment"
                                    value="cod"
                                    checked={paymentMethod === "cod"}
                                    onChange={() => setPaymentMethod("cod")}
                                    className="sr-only"
                                />
                                <div className={`h-11 w-11 rounded-full flex items-center justify-center ${paymentMethod === "cod" ? "bg-[#121214] text-white" : "bg-secondary text-foreground"}`}>
                                    <Banknote className="h-5 w-5" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium">Cash on delivery</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">Pay the delivery agent in cash when your parcel arrives. Extra ₹{COD_FEE} handling fee.</p>
                                </div>
                                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === "cod" ? "border-foreground" : "border-muted-foreground/40"}`}>
                                    {paymentMethod === "cod" && <span className="h-2.5 w-2.5 rounded-full bg-foreground" />}
                                </div>
                            </label>
                        </div>
                    </section>
                </div>

                <aside>
                    <div className="sticky top-32 dw-section p-6 space-y-4" data-testid="checkout-summary">
                        <h3 className="font-display text-2xl">Summary</h3>
                        <div className="max-h-[260px] overflow-y-auto space-y-3 pr-1">
                            {preview.items.map(it => (
                                <div key={it.variant_sku} className="flex gap-3 items-center">
                                    <div className="relative h-14 w-14 rounded-xl overflow-hidden border border-[#E7DED2]">
                                        <img src={it.image} alt="" className="w-full h-full object-cover" />
                                        <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 text-[10px] rounded-full bg-[#121214] text-white flex items-center justify-center">{it.qty}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm truncate">{it.title}</p>
                                        <p className="text-xs text-muted-foreground">{it.color} / {it.size}</p>
                                    </div>
                                    <p className="text-sm font-medium">{formatPrice(it.line_total)}</p>
                                </div>
                            ))}
                        </div>
                        
                        {/* Coupon */}
                        <div className="rounded-2xl border border-dashed border-[#E7DED2] p-3 flex gap-2 items-center">
                            <Tag className="h-4 w-4 text-muted-foreground" />
                            <input type="text" placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} className="flex-1 bg-transparent outline-none text-sm uppercase" data-testid="checkout-coupon-input" />
                            <button type="button" onClick={handleApplyCoupon} className="text-xs font-mono-ui hover:underline" data-testid="checkout-apply-coupon">Apply</button>
                        </div>
                        {appliedCoupon && (
                            <div className="text-xs text-[#2F7D5A] flex justify-between">
                                <span>{appliedCoupon.code} — {appliedCoupon.description}</span>
                                <span>− {formatPrice(discount)}</span>
                            </div>
                        )}

                        <div className="h-px bg-[#E7DED2]" />
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(preview.subtotal)}</span></div>
                            <div className="flex justify-between"><span>Shipping</span><span>{preview.shipping === 0 ? <span className="text-[#2F7D5A]">Free</span> : formatPrice(preview.shipping)}</span></div>
                            {discount > 0 && <div className="flex justify-between text-[#2F7D5A]"><span>Discount</span><span>− {formatPrice(discount)}</span></div>}
                            {paymentMethod === "cod" && (
                                <div className="flex justify-between text-muted-foreground">
                                    <span>COD handling</span>
                                    <span>{formatPrice(COD_FEE * 100)}</span>
                                </div>
                            )}
                        </div>
                        <div className="h-px bg-[#E7DED2]" />
                        <div className="flex justify-between font-semibold text-lg">
                            <span>Total</span><span data-testid="checkout-total">{formatPrice(finalTotal)}</span>
                        </div>
                        <button type="submit" disabled={submitting} className="w-full dw-btn-primary h-12 font-mono-ui inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkout-pay-button">
                            {paymentMethod === "cod"
                                ? <><Banknote className="h-4 w-4" /> {submitting ? "Placing…" : `Place order — ${formatPrice(finalTotal)}`}</>
                                : <><ShieldCheck className="h-4 w-4" /> {submitting ? "Processing…" : `Pay ${formatPrice(finalTotal)}`}</>
                            }
                        </button>
                        <p className="text-[11px] text-center text-muted-foreground">By placing the order you agree to X-Plosiv&apos;s terms.</p>
                    </div>
                </aside>
            </form>
            <div className="h-24" />
        </div>
    );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
    return (
        <div>
            <Label className="font-mono-ui mb-2 block">{label}{required && " *"}</Label>
            <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="rounded-2xl bg-white/70 border-[#E7DED2] h-11" data-testid={testid} />
        </div>
    );
}
